import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, pagerApi } from "@/api";
import { useRealtime } from "@/hooks/useRealtime";
import { PRIORITIES } from "@/lib/constants";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BellRing, Radio, Volume2, Power, MapPin, Truck, Clock, Check, KeyRound, ScanLine, LogOut, Ban, CheckCheck, Building2 } from "lucide-react";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return now;
}

const pagerToken = () => localStorage.getItem("pagerToken");

export default function PagerApp() {
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const now = useClock();
  const [phase, setPhase] = useState("loading"); // loading | enroll | app
  const [label, setLabel] = useState("Pager");
  const [locId, setLocId] = useState(null);
  const [locName, setLocName] = useState("");
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [scanning, setScanning] = useState(false);
  const [armed, setArmed] = useState(false);
  const [online, setOnline] = useState(false);
  const [lastMsgAt, setLastMsgAt] = useState(0);
  const [wsStale, setWsStale] = useState(false);
  const wsStatus = !online ? "connecting" : wsStale ? "degraded" : "live";
  const [alerts, setAlerts] = useState([]);
  const [active, setActive] = useState(null);
  const [acksById, setAcksById] = useState({});
  const [retractNotice, setRetractNotice] = useState(null);
  const [pagerMessage, setPagerMessage] = useState(null);
  const [requirePagerAck, setRequirePagerAck] = useState(true);
  const [pushOn, setPushOn] = useState(false);
  const armedRef = useRef(false);
  const alertsRef = useRef([]); alertsRef.current = alerts;
  const firstWsOpenRef = useRef(true);
  const locRef = useRef(null); locRef.current = locId;
  const audioCtxRef = useRef(null);
  const alarmStopRef = useRef(null);
  const videoRef = useRef(null);
  const scanStopRef = useRef(null);

  const loadInfo = useCallback(async (lid) => {
    if (!lid) { setLocName(""); return; }
    try {
      const { data } = await axios.get(`${API}/pager/status`, { params: { pager_id: localStorage.getItem("pagerDeviceId") } });
      setLocName(data.location_name || "");
    } catch (e) { /* optional */ }
  }, []);

  // Bootstrap: known device -> straight in; otherwise ask for the pairing code.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const urlTok = params.get("token");
      if (urlTok) localStorage.setItem("pagerToken", urlTok);
      const tok = pagerToken();
      if (tok) {
        try {
          const { data } = await pagerApi.get("/auth/me");
          if (cancelled) return;
          setLabel(data.name || "Pager"); setLocId(data.location_id || null); loadInfo(data.location_id);
          setPhase("app"); return;
        } catch (e) { localStorage.removeItem("pagerToken"); }
      }
      const pid = localStorage.getItem("pagerDeviceId");
      if (pid) {
        try {
          const { data } = await axios.get(`${API}/pager/status`, { params: { pager_id: pid } });
          if (cancelled) return;
          if (data.status === "approved" && data.token) {
            localStorage.setItem("pagerToken", data.token);
            setLabel(data.label || "Pager"); setLocId(data.location_id || null); loadInfo(data.location_id);
            setPhase("app"); return;
          }
          localStorage.removeItem("pagerDeviceId");
        } catch (e) { localStorage.removeItem("pagerDeviceId"); }
      }
      if (!cancelled) setPhase("enroll");
    })();
    return () => { cancelled = true; };
  }, [loadInfo]);

  const doEnroll = async (raw) => {
    const c = (raw || "").trim().toUpperCase();
    if (!c) return;
    try {
      const existing = localStorage.getItem("pagerDeviceId") || undefined;
      const { data } = await axios.post(`${API}/pager/enroll`, { code: c, device_id: existing });
      localStorage.setItem("pagerToken", data.token);
      localStorage.setItem("pagerDeviceId", data.pager_id);
      setLabel(data.label || "Pager");
      const me = await pagerApi.get("/auth/me").then((r) => r.data).catch(() => ({}));
      setLocId(me.location_id || null); loadInfo(me.location_id);
      setPhase("app");
      toast.success("Pager gekoppeld");
    } catch (err) { toast.error(err.response?.data?.detail || "Koppelen mislukt"); }
  };
  const enroll = (e) => { if (e && e.preventDefault) e.preventDefault(); doEnroll(code); };

  const parseCode = (raw) => {
    if (!raw) return "";
    try { const u = new URL(raw); const c = u.searchParams.get("code"); if (c) return c.toUpperCase(); } catch (_) {}
    const m = String(raw).match(/[A-Za-z0-9]{4,8}/);
    return (m ? m[0] : String(raw)).toUpperCase();
  };
  const stopScan = () => { setScanning(false); if (scanStopRef.current) { scanStopRef.current(); scanStopRef.current = null; } };
  const startScan = async () => {
    if (!("BarcodeDetector" in window)) { toast.error("Scannen wordt niet ondersteund — voer de code handmatig in."); return; }
    let stream; let raf; let cancelled = false;
    try { stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } }); }
    catch (e) { toast.error("Geen toegang tot de camera."); return; }
    setScanning(true);
    scanStopRef.current = () => { cancelled = true; if (raf) cancelAnimationFrame(raf); stream.getTracks().forEach((t) => t.stop()); };
    const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
    setTimeout(async () => {
      const v = videoRef.current;
      if (v) { v.srcObject = stream; try { await v.play(); } catch (_) {} }
      const tick = async () => {
        if (cancelled || !videoRef.current) return;
        try {
          const found = await detector.detect(videoRef.current);
          if (found && found.length) { const c = parseCode(found[0].rawValue); if (c) { setCode(c); stopScan(); toast.success(`Code gescand: ${c}`); doEnroll(c); return; } }
        } catch (_) { /* keep scanning */ }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, 100);
  };
  useEffect(() => () => { if (scanStopRef.current) scanStopRef.current(); }, []);

  const unpair = () => {
    stopScan();
    localStorage.removeItem("pagerToken"); localStorage.removeItem("pagerDeviceId");
    setArmed(false); armedRef.current = false; setAlerts([]); setActive(null); setCode(""); setPhase("enroll");
  };

  const ensureAudio = () => {
    if (!audioCtxRef.current) { const Ctx = window.AudioContext || window.webkitAudioContext; if (Ctx) audioCtxRef.current = new Ctx(); }
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") audioCtxRef.current.resume();
    return audioCtxRef.current;
  };
  const stopAlarm = useCallback(() => {
    if (alarmStopRef.current) { alarmStopRef.current(); alarmStopRef.current = null; }
    try { navigator.vibrate && navigator.vibrate(0); } catch (_) {}
  }, []);
  const playAlarm = useCallback((priority = 2) => {
    const ctx = ensureAudio(); if (!ctx) return; try { ctx.resume && ctx.resume(); } catch (_) {} stopAlarm();
    const now0 = ctx.currentTime; const critical = priority <= 1;
    const beepDur = critical ? 0.16 : 0.24; const gap = critical ? 0.10 : 0.22;
    const freqA = critical ? 1100 : 860; const freqB = critical ? 820 : 640;
    const cycle = beepDur + gap; const count = Math.max(1, Math.floor(5 / cycle)); const oscs = [];
    for (let i = 0; i < count; i++) {
      const t = now0 + i * cycle; const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.type = "square"; osc.frequency.setValueAtTime(i % 2 === 0 ? freqA : freqB, t);
      gain.gain.setValueAtTime(0.0001, t); gain.gain.exponentialRampToValueAtTime(0.5, t + 0.008); gain.gain.exponentialRampToValueAtTime(0.0001, t + beepDur);
      osc.connect(gain).connect(ctx.destination); osc.start(t); osc.stop(t + beepDur + 0.03); oscs.push(osc);
    }
    try { const pattern = []; for (let i = 0; i < count; i++) { pattern.push(Math.round(beepDur * 1000), Math.round(gap * 1000)); } navigator.vibrate && navigator.vibrate(pattern); } catch (_) {}
    alarmStopRef.current = () => { oscs.forEach((o) => { try { o.stop(); } catch (_) {} }); };
  }, [stopAlarm]);

  const matchesMyLoc = useCallback((p) => {
    const myLoc = locRef.current;
    if (!myLoc) return true;
    const locSet = new Set([
      ...((p.vehicles || []).map((v) => v.location_id)),
      ...((p.vehicles || []).map((v) => v.location_ids || []).flat()),
      p.building?.location_id, p.location_id,
    ].filter(Boolean));
    return !(locSet.size > 0 && !locSet.has(myLoc));
  }, []);

  const pushAlert = useCallback((p, extra = false, addedCallsigns = []) => {
    const alert = {
      id: p.id, ref: p.ref, priority: p.priority || 4, title: p.title,
      subcategory: p.subcategory || "",
      location: p.building?.name || p.location_label || "Onbekende locatie",
      functionLabel: p.building?.function || "",
      vehicles: (p.vehicles || []).map((v) => v.callsign).join(", "),
      extra, addedCallsigns,
      at: p.created_at || new Date().toISOString(),
    };
    setAlerts((a) => [alert, ...a.filter((x) => x.id !== alert.id)].slice(0, 30));
    setActive(alert);
    if (armedRef.current) playAlarm(alert.priority);
  }, [playAlarm]);

  const silentAdd = useCallback((p) => {
    const alert = {
      id: p.id, ref: p.ref, priority: p.priority || 4, title: p.title,
      subcategory: p.subcategory || "",
      location: p.building?.name || p.location_label || "Onbekende locatie",
      functionLabel: p.building?.function || "",
      vehicles: (p.vehicles || []).map((v) => v.callsign).join(", "),
      extra: false, addedCallsigns: [], at: p.created_at || new Date().toISOString(),
    };
    setAlerts((a) => [alert, ...a.filter((x) => x.id !== alert.id)].slice(0, 30));
  }, []);

  useRealtime((msg) => {
    if (msg && msg.type) { setOnline(true); setLastMsgAt(Date.now()); setWsStale(false); }
    { const myEnv = localStorage.getItem("pagerEnvId"); if (msg.payload && msg.payload.env_id && myEnv && msg.payload.env_id !== myEnv) return; }
    if (msg.type === "__ws_open") {
      pagerApi.get("/incidents?status=open").then(({ data }) => {
        const list = (data || []).filter(matchesMyLoc);
        const known = new Set(alertsRef.current.map((a) => a.id));
        const missed = list.filter((p) => !known.has(p.id));
        if (firstWsOpenRef.current) { missed.forEach(silentAdd); firstWsOpenRef.current = false; }
        else { missed.forEach((p) => pushAlert(p, false)); }
      }).catch(() => {});
      return;
    }
    if (msg.type === "incident_created" && msg.payload) {
      if (!matchesMyLoc(msg.payload)) return;
      pushAlert(msg.payload, false);
    }
    if (msg.type === "incident_repaged" && msg.payload) {
      if (!matchesMyLoc(msg.payload)) return;
      pushAlert(msg.payload, true, msg.payload.added_callsigns || []);
    }
    if (msg.type === "incident_updated" && msg.payload) {
      const p = msg.payload;
      setAlerts((a) => a.map((x) => (x.id === p.id ? { ...x, subcategory: p.subcategory || "", title: p.title } : x)));
      setActive((a) => (a && a.id === p.id ? { ...a, subcategory: p.subcategory || "", title: p.title } : a));
    }
    if (msg.type === "incident_ack" && msg.payload) {
      setAcksById((m) => ({ ...m, [msg.payload.incident_id]: msg.payload.acks || [] }));
    }
    if (msg.type === "incident_retracted" && msg.payload) {
      const p = msg.payload.incident || {};
      const known = alertsRef.current.some((a) => a.id === msg.payload.incident_id);
      if (!known && !matchesMyLoc(p)) return;
      setRetractNotice({ id: msg.payload.incident_id, title: p.title || "Melding", scope: msg.payload.scope, callsigns: msg.payload.callsigns || [] });
      if (armedRef.current) { playAlarm(4); setTimeout(stopAlarm, 800); }
      if (msg.payload.scope === "all") setActive((a) => (a && a.id === msg.payload.incident_id ? null : a));
    }
    if (msg.type === "pager_message" && msg.payload) {
      const p = msg.payload;
      const myId = localStorage.getItem("pagerDeviceId");
      const ids = p.pager_ids || [];
      if (ids.length && myId && !ids.includes(myId)) return;
      setPagerMessage({ id: p.id, title: p.title, text: p.text, kind: p.kind, at: p.at });
      if (armedRef.current) playAlarm(p.kind === "test" ? 4 : 2);
    }
  }, pagerToken());
  useEffect(() => {
    const t = setInterval(() => setWsStale(lastMsgAt > 0 && Date.now() - lastMsgAt > 35000), 5000);
    return () => clearInterval(t);
  }, [lastMsgAt]);

  const arm = () => { ensureAudio(); armedRef.current = true; setArmed(true); playAlarm(3); setTimeout(stopAlarm, 600); };
  const acknowledge = async () => {
    stopAlarm();
    const cur = active;
    setActive(null);
    if (cur && cur.id && cur.id !== "test" && requirePagerAck) { try { await pagerApi.post(`/incidents/${cur.id}/ack`); } catch (_) {} }
  };

  useEffect(() => {
    if (phase !== "app") return;
    pagerApi.get("/settings/ack-policy").then(({ data }) => setRequirePagerAck(data.require_pager_ack !== false)).catch(() => {});
  }, [phase]);

  useEffect(() => {
    if (phase !== "app") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    navigator.serviceWorker.register("/sw.js").then((reg) => reg.pushManager.getSubscription()).then((sub) => setPushOn(!!sub)).catch(() => {});
  }, [phase]);

  const enablePush = async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) { toast.error("Web-push wordt niet ondersteund op dit apparaat"); return; }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Meldingen geweigerd in de browser"); return; }
      const reg = await navigator.serviceWorker.register("/sw.js"); await navigator.serviceWorker.ready;
      const { data } = await pagerApi.get("/push/public-key");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(data.publicKey) });
      await pagerApi.post("/push/subscribe", { subscription: sub.toJSON() });
      setPushOn(true); toast.success("Meldingen aan — ook als het scherm uit staat");
    } catch (err) { toast.error("Inschakelen mislukt"); }
  };
  const disablePush = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) { await pagerApi.post("/push/unsubscribe", { endpoint: sub.endpoint }); await sub.unsubscribe(); }
      setPushOn(false); toast.success("Meldingen uit");
    } catch (_) {}
  };
  const testAlarm = () => { setActive({ id: "test", ref: "TEST", priority: 1, title: "Testmelding pager", location: "Kazerne", vehicles: "—", at: new Date().toISOString() }); playAlarm(1); };

  const timeStr = now.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const dateStr = now.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });

  if (phase === "loading") {
    return <div className="flex h-screen items-center justify-center bg-slate-950 text-slate-400">{t("dev.loading")}</div>;
  }

  if (phase === "enroll") {
    return (
      <div data-testid="pager-enroll" className="relative flex min-h-screen items-center justify-center bg-slate-950 p-6">
        <div className="absolute right-5 top-4"><LanguageSwitcher dark /></div>
        <form onSubmit={enroll} className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-900 p-6 text-white">
          <div className="mb-1 flex items-center gap-2 text-lg font-bold"><BellRing className="h-5 w-5 text-amber-400" /> DispatchMe Pager</div>
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-300"><KeyRound className="h-4 w-4" /> {t("dev.enterCode")}</div>
          <p className="mb-3 text-xs text-slate-400">{t("dev.pagerEnrollHint")}</p>
          {scanning ? (
            <div className="space-y-3">
              <video ref={videoRef} data-testid="pager-scan-video" playsInline muted className="h-52 w-full rounded-xl border border-slate-700 bg-black object-cover" />
              <button type="button" data-testid="pager-scan-stop" onClick={stopScan} className="w-full rounded-xl border border-slate-700 py-2.5 text-sm font-semibold text-slate-300 hover:bg-slate-800">{t("dev.stopScan")}</button>
            </div>
          ) : (
            <>
              <input data-testid="pager-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Bijv. 7GX2QP"
                className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-3 text-center font-mono text-lg tracking-[0.3em] text-white outline-none focus:border-blue-500" />
              <button type="submit" data-testid="pager-enroll-btn" className="mt-3 w-full rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-500">{t("dev.pairDevice")}</button>
              <button type="button" data-testid="pager-scan-btn" onClick={startScan} className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-700 py-3 text-sm font-semibold text-slate-300 hover:bg-slate-800"><ScanLine className="h-4 w-4" /> {t("dev.scanQr")}</button>
            </>
          )}
        </form>
      </div>
    );
  }

  if (!armed) {
    return (
      <div data-testid="pager-arm-screen" className="relative flex h-screen flex-col items-center justify-center bg-slate-950 px-6 text-center text-white">
        <div className="absolute right-5 top-4"><LanguageSwitcher dark /></div>
        <BellRing className="mb-6 h-16 w-16 text-amber-400" />
        <h1 className="font-display text-3xl font-bold">DispatchMe Pager</h1>
        <p className="mt-3 max-w-sm text-sm text-slate-400">{t("dev.pagerArmHint")}</p>
        <button data-testid="pager-arm-btn" onClick={arm}
          className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-amber-500 px-8 py-4 text-lg font-bold text-slate-950 transition-transform hover:scale-[1.03] active:scale-95">
          <Power className="h-5 w-5" /> Pager activeren
        </button>
        <button onClick={unpair} className="mt-6 text-xs text-slate-500 hover:text-slate-300">{t("dev.unpair")}</button>
      </div>
    );
  }

  if (pagerMessage) {
    const test = pagerMessage.kind === "test";
    return (
      <div data-testid="pager-message" className="flex h-screen flex-col text-white pager-flash" style={{ backgroundColor: test ? "#b45309" : "#1d4ed8" }}>
        <div className="flex items-center justify-between px-6 py-4">
          <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest opacity-90"><Radio className="h-5 w-5 animate-pulse" /> {test ? "Testbericht" : "Pager-oproep"}</span>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
          {pagerMessage.title && <div data-testid="pager-message-title" className="rounded-2xl bg-white/20 px-6 py-2 text-xl font-black uppercase tracking-wide backdrop-blur">{pagerMessage.title}</div>}
          <p data-testid="pager-message-text" className="mt-6 max-w-xl font-display text-3xl font-black leading-tight">{pagerMessage.text}</p>
        </div>
        <div className="px-6 pb-8">
          <button data-testid="pager-message-ack-btn" onClick={() => { stopAlarm(); setPagerMessage(null); }} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white/95 py-5 text-xl font-bold text-slate-900 transition-transform active:scale-95"><Check className="h-6 w-6" /> Begrepen</button>
        </div>
      </div>
    );
  }

  if (retractNotice) {
    return (
      <div data-testid="pager-retracted" className="flex h-screen flex-col bg-slate-800 text-white">
        <div className="flex items-center justify-between px-6 py-4">
          <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest opacity-90"><Ban className="h-5 w-5" /> Ingetrokken</span>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
          <Ban className="mb-6 h-16 w-16 text-amber-400" />
          <div className="rounded-2xl bg-white/10 px-6 py-2 text-lg font-bold uppercase tracking-wide">{retractNotice.scope === "all" ? "Melding ingetrokken" : "Voertuig ingetrokken"}</div>
          <h1 data-testid="pager-retracted-title" className="mt-6 font-display text-3xl font-black leading-tight">{retractNotice.title}</h1>
          {retractNotice.scope !== "all" && retractNotice.callsigns.length > 0 && (
            <div className="mt-4 text-lg font-semibold">{retractNotice.callsigns.join(", ")}</div>
          )}
        </div>
        <div className="px-6 pb-8">
          <button data-testid="pager-retract-ack-btn" onClick={() => setRetractNotice(null)} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white/95 py-5 text-xl font-bold text-slate-900 transition-transform active:scale-95"><Check className="h-6 w-6" /> Begrepen</button>
        </div>
      </div>
    );
  }

  if (active) {
    const P = PRIORITIES[active.priority] || PRIORITIES[4];
    return (
      <div data-testid="pager-alert" className="flex h-screen flex-col text-white pager-flash" style={{ backgroundColor: P.hex }}>
        <div className="flex items-center justify-between px-6 py-4">
          <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest opacity-90"><BellRing className="h-5 w-5 animate-pulse" /> Uitruk</span>
          <span className="font-mono text-sm opacity-90">{active.ref}</span>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
          <div data-testid="pager-alert-prio" className="rounded-2xl bg-white/20 px-6 py-2 text-2xl font-black uppercase tracking-wide backdrop-blur">{P.short} · {P.label}</div>
          <h1 data-testid="pager-alert-title" className="mt-6 font-display text-4xl font-black leading-tight sm:text-5xl">{active.title}{active.subcategory ? ` — ${active.subcategory}` : ""}</h1>
          <div className="mt-6 flex flex-col items-center gap-2 text-lg font-semibold">
            <span className="inline-flex items-center gap-2"><MapPin className="h-5 w-5" /> {active.location}</span>
            {active.functionLabel && <span data-testid="pager-alert-function" className="inline-flex items-center gap-2"><Building2 className="h-5 w-5" /> {active.functionLabel}</span>}
            <span className="inline-flex items-center gap-2"><Truck className="h-5 w-5" /> voor {active.vehicles || "—"}</span>
          </div>
          {active.extra && active.addedCallsigns && active.addedCallsigns.length > 0 && (
            <div data-testid="pager-extra-badge" className="mt-4 rounded-xl bg-black/25 px-4 py-2 text-sm font-bold uppercase tracking-wide backdrop-blur">Extra gealarmeerd · {active.addedCallsigns.join(", ")}</div>
          )}
          {(acksById[active.id] || []).length > 0 && (
            <div data-testid="pager-acks" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white/20 px-4 py-2 text-sm font-semibold backdrop-blur">
              <CheckCheck className="h-4 w-4" /> Bevestigd: {(acksById[active.id] || []).map((a) => a.name).join(", ")}
            </div>
          )}
        </div>
        <div className="px-6 pb-8">
          <button data-testid="pager-ack-btn" onClick={acknowledge} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white/95 py-5 text-xl font-bold text-slate-900 transition-transform active:scale-95"><Check className="h-6 w-6" /> Bevestigen</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pager-standby" className="flex h-screen flex-col bg-slate-950 text-white">
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
        <span className="inline-flex items-center gap-2 font-display text-sm font-bold"><BellRing className="h-4 w-4 text-amber-400" /> DispatchMe Pager</span>
        <div className="flex items-center gap-3">
          <LanguageSwitcher dark />
          <span data-testid="pager-online" className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${wsStatus === "live" ? "bg-emerald-500/20 text-emerald-300" : wsStatus === "degraded" ? "bg-amber-500/20 text-amber-300" : "bg-white/10 text-slate-400"}`}>
            <Radio className="h-3.5 w-3.5" /> {wsStatus === "live" ? "Live" : wsStatus === "degraded" ? "Herverbinden…" : "Verbinden…"}
          </span>
          <button data-testid="pager-push-btn" onClick={pushOn ? disablePush : enablePush}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold ${pushOn ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 hover:bg-white/20"}`}>
            <BellRing className="h-3.5 w-3.5" /> {pushOn ? "Push aan" : "Push inschakelen"}
          </button>
          <button data-testid="pager-test-btn" onClick={testAlarm} className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1 text-xs font-semibold hover:bg-white/20"><Volume2 className="h-3.5 w-3.5" /> Test</button>
          <button data-testid="pager-unpair-btn" onClick={unpair} className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1 text-xs font-semibold hover:bg-white/20"><LogOut className="h-3.5 w-3.5" /> Ontkoppelen</button>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center py-10">
        <div className="font-mono text-7xl font-black tracking-tight tabular-nums">{timeStr}</div>
        <div className="mt-2 text-sm capitalize text-slate-400">{dateStr}</div>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-4 py-1.5 text-sm font-semibold text-emerald-300">
          <span className="h-2 w-2 rounded-full bg-emerald-400 live-dot" /> Standby — wacht op meldingen
        </div>
        <div className="mt-4 text-center text-xs text-slate-400" data-testid="pager-binding">
          <span className="font-semibold text-slate-200">{label}</span>
          <div className="mt-1 inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {locName || "Alle locaties (geen locatie gekoppeld)"}</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-6">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"><Clock className="h-3.5 w-3.5" /> Recente meldingen</div>
        {alerts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 py-8 text-center text-sm text-slate-500">Nog geen meldingen ontvangen.</div>
        ) : (
          <div className="space-y-2">
            {alerts.map((a, i) => {
              const P = PRIORITIES[a.priority] || PRIORITIES[4];
              return (
                <button key={`${a.id}-${i}`} data-testid={`pager-recent-${a.id}`} onClick={() => setActive(a)}
                  className="flex w-full items-center gap-3 rounded-xl bg-white/5 p-3 text-left hover:bg-white/10">
                  <span className="rounded-md px-2 py-1 text-xs font-bold" style={{ backgroundColor: P.hex }}>{P.short}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{a.title}</div>
                    <div className="truncate text-xs text-slate-400">{a.location}{a.functionLabel ? ` (${a.functionLabel})` : ""} · voor {a.vehicles || "—"}</div>
                  </div>
                  <span className="font-mono text-[11px] text-slate-500">{new Date(a.at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
